import type {
  ForgeAppView,
  ForgeProvider,
  SetForgeAppRequest,
} from '@/lib/api/generated/model';

import { FORGE_APPS_COPY as COPY } from './linkedAccountsCopy';

/**
 * Each forge host's sign-in app, as Admin → Linked accounts sets it up (spec
 * 161). Pure: no React, no client.
 */

export function appStatusWords(app: Pick<ForgeAppView, 'configured'>): string {
  return app.configured ? COPY.setUp : COPY.notSetUp;
}

/** What an admin does on the forge, in its own words, and where it starts. */
export interface AppSetup {
  steps: string[];
  /** Where the forge creates the app, by its URL convention. */
  createUrl: string;
  idLabel: string;
  secretLabel: string;
}

// Copy drafted by AI; reviewed and approved by the user 2026-10-13 (spec 161).
export function appSetupSteps(provider: ForgeProvider, host: string): AppSetup {
  if (provider === 'gitlab') {
    return {
      steps: [
        `On ${host}, open Admin → Applications → New application.`,
        'Paste the redirect address above as the Redirect URI.',
        'Tick Confidential, and the scopes api and read_user.',
        'Save it, then copy its Application ID and Secret here.',
      ],
      createUrl: `https://${host}/admin/applications/new`,
      idLabel: 'Application ID',
      secretLabel: 'Secret',
    };
  }
  return {
    steps: [
      `On ${host}, open Settings → Developer settings → GitHub Apps → New GitHub App.`,
      "Paste the redirect address above as the Callback URL, and keep 'Expire user authorization tokens' on.",
      'Under Repository permissions, set Contents and Pull requests to Read and write.',
      'Create it, generate a client secret, and copy its Client ID and the secret here.',
      "Install the app on your organization's repositories. Without it, people can sign in but runs cannot push.",
    ],
    createUrl: `https://${host}/settings/apps/new`,
    idLabel: 'Client ID',
    secretLabel: 'Client secret',
  };
}

/**
 * The body to save. An empty secret is left out, which keeps the stored one —
 * the service refuses that only for a host with no app yet.
 */
export function appRequest(
  clientId: string,
  secret: string,
): SetForgeAppRequest {
  const trimmed = secret.trim();
  return trimmed
    ? { client_id: clientId.trim(), client_secret: trimmed }
    : { client_id: clientId.trim() };
}

/**
 * Why the service refused a save, from its 422 body: `{ violations: [{ path:
 * 'client_secret', message }] }`. Read defensively — the generated type for
 * this status says nothing.
 */
export function appRefusal(
  body: unknown,
): { field: 'client_secret'; message: string } | null {
  if (typeof body !== 'object' || body === null || !('violations' in body)) {
    return null;
  }
  const { violations } = body;
  if (!Array.isArray(violations)) return null;
  for (const violation of violations as unknown[]) {
    if (typeof violation !== 'object' || violation === null) continue;
    const { path, message } = violation as {
      path?: unknown;
      message?: unknown;
    };
    if (path === 'client_secret' && typeof message === 'string') {
      return { field: 'client_secret', message };
    }
  }
  return null;
}
